import { Button, Card, Input, Space, Tabs, Tag, Upload, message } from 'antd';
import React, { useCallback, useState } from 'react';

import Text from '@/atoms/Text';
import { config } from '@/config';
import { useAuthContext } from '@/hooks/auth.store';
import { InboxIcon as InboxOutlined } from '@/icons';

const { Dragger } = Upload;

export interface InputMethodCardProps {
  onSourceChange?: (source: 'manual' | 'llm') => void;
  clientId?: string;
  projectId?: string;
  disabled?: boolean;
  onFieldsExtracted?: (fields: {
    accomplishments?: string;
    planNextWeek?: string;
    riskBlockers?: string;
    onScheduleStatus?: string;
    csatStatus?: string;
    teamHealthStatus?: string;
    notes?: string;
  }) => void;
}

interface LlmActionRowProps {
  loading?: boolean;
  notes?: string;
  disabled?: boolean;
  onExtract?: () => void;
}

const LlmActionRow: React.FC<LlmActionRowProps> = ({
  loading = false,
  notes = '',
  disabled = false,
  onExtract,
}) => (
  <div className="input-method__action-row">
    <div className="input-method__action-copy">
      <Text variant="bodySmall" color="var(--text-secondary)">
        Pre-fills health, notes, free-text, and milestone changes. You review &amp; edit before
        saving.
      </Text>
      <Tag className="bdt-source--llm">Source: LLM-generated</Tag>
    </div>
    <Button
      type="primary"
      className="card__llm-button"
      icon={<i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />}
      disabled={disabled || !notes || notes.trim().length === 0}
      loading={loading}
      onClick={onExtract}
    >
      Generate Update Status
    </Button>
  </div>
);

const createPasteTextContent = (
  onNotesChange: (value: string) => void,
  loading: boolean,
  notes: string,
  onExtract: () => void,
  disabled?: boolean,
) => (
  <Space direction="vertical" size="middle" className="input-method__full-width">
    <Input.TextArea
      rows={8}
      placeholder="Paste your weekly status notes here. Anything works — bullets, a draft email, a paragraph."
      className="input-method__textarea"
      onChange={(e) => onNotesChange(e.target.value)}
      value={notes}
      disabled={disabled}
    />
    <LlmActionRow loading={loading} notes={notes} onExtract={onExtract} disabled={disabled} />
  </Space>
);

const createUploadFileContent = (
  onFileSelect: (file: File) => void,
  loading: boolean,
  selectedFile: File | null,
  onExtract: () => void,
  disabled?: boolean,
) => (
  <Space direction="vertical" size="large" className="input-method__full-width">
    <Dragger
      name="file"
      multiple={false}
      accept=".pdf,.ppt,.pptx,.doc,.docx"
      className="input-method__dragger"
      beforeUpload={(file) => {
        onFileSelect(file);
        return false; // Prevent default upload behavior
      }}
      maxCount={1}
      showUploadList={false}
      disabled={disabled}
    >
      <p className="input-method__dragger-icon">
        <InboxOutlined />
      </p>
      <p className="input-method__dragger-text">Drop file here or browse</p>
      <p className="input-method__dragger-text">PDF · PPT · PPTX · DOC · DOCX — max 10 MB</p>
    </Dragger>
    {selectedFile && (
      <div className="input-method__file-info">
        <Text variant="bodySmall" color="var(--text-secondary)">
          Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
        </Text>
      </div>
    )}
    <LlmActionRow
      loading={loading}
      notes={selectedFile?.name || ''}
      disabled={disabled}
      onExtract={onExtract}
    />
  </Space>
);

const createFillDirectlyContent = () => (
  <div className="input-method__action-row">
    <div className="input-method__action-copy">
      <Text variant="bodySmall" color="var(--text-secondary)">
        Skip extraction and fill the form below directly.
      </Text>
    </div>
  </div>
);

export function InputMethodCard({
  onSourceChange,
  onFieldsExtracted,
  clientId = '',
  projectId = '',
  disabled = false,
}: Readonly<InputMethodCardProps>): React.ReactElement {
  const { accessToken } = useAuthContext();
  const [activeTab, setActiveTab] = useState('paste-text');

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };
  const [pasteNotes, setPasteNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);

  const handleExtractFromNotes = useCallback(async () => {
    if (!pasteNotes.trim() || !clientId || !projectId) {
      return;
    }

    try {
      setExtracting(true);

      const response = await fetch(
        `${config.apiUrl}/clients/${clientId}/projects/${projectId}/extract-from-notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ notes: pasteNotes }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to extract from notes');
      }

      const data = await response.json();

      // Call callback with all extracted fields (including three separate health statuses)
      onFieldsExtracted?.({
        accomplishments: data.accomplishments || undefined,
        planNextWeek: data.plan_next_week || undefined,
        riskBlockers: data.risk_blockers || undefined,
        onScheduleStatus: data.on_schedule_status || undefined,
        csatStatus: data.csat_status || undefined,
        teamHealthStatus: data.team_health_status || undefined,
        notes: data.extracted_notes || undefined,
      });

      // Update source tag
      onSourceChange?.('llm');
    } catch {
      message.error('Failed to extract from notes. Please try again or fill in manually.');
    } finally {
      setExtracting(false);
    }
  }, [pasteNotes, clientId, projectId, accessToken, onFieldsExtracted, onSourceChange]);

  const handleExtractFromDocument = useCallback(async () => {
    if (!selectedFile) {
      return;
    }

    if (!clientId || !projectId) {
      return;
    }

    try {
      setExtracting(true);

      const formData = new FormData();
      formData.append('file', selectedFile);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

      const response = await fetch(
        `${config.apiUrl}/clients/${clientId}/projects/${projectId}/extract-from-document`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      // Call callback with all extracted fields
      onFieldsExtracted?.({
        accomplishments: data.accomplishments || undefined,
        planNextWeek: data.plan_next_week || undefined,
        riskBlockers: data.risk_blockers || undefined,
        onScheduleStatus: data.on_schedule_status || undefined,
        csatStatus: data.csat_status || undefined,
        teamHealthStatus: data.team_health_status || undefined,
        notes: data.extracted_notes || undefined,
      });

      // Update source tag
      onSourceChange?.('llm');
    } catch {
      message.error('Failed to extract from document. Please try again or fill in manually.');
    } finally {
      setExtracting(false);
    }
  }, [selectedFile, clientId, projectId, accessToken, onFieldsExtracted, onSourceChange]);

  const pasteTextContent = createPasteTextContent(
    setPasteNotes,
    extracting,
    pasteNotes,
    handleExtractFromNotes,
    disabled,
  );

  const uploadFileContent = createUploadFileContent(
    setSelectedFile,
    extracting,
    selectedFile,
    handleExtractFromDocument,
    disabled,
  );

  const fillDirectlyContent = createFillDirectlyContent();

  return (
    <Card variant="borderless" className="input-method-card">
      <div className="card__header">
        <Text variant="h5">Input Method</Text>
        <Tag className="card__source-tag">Source: Manual</Tag>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'upload',
            label: 'Upload file',
            children: uploadFileContent,
          },
          {
            key: 'paste-text',
            label: 'Paste text',
            children: pasteTextContent,
          },
          {
            key: 'fill-directly',
            label: 'Fill directly',
            children: fillDirectlyContent,
          },
        ]}
      />
    </Card>
  );
}
